import { useEffect, useRef, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import AuthField from "@/components/shared/AuthField";
import { resolveRoute, userHome, backTarget } from "@/lib/guards";
import { friendlyError } from "@/lib/errors";
import { Lock, Sparkles, MailCheck } from "lucide-react";
import { toast } from "sonner";

const RESEND_SECONDS = 60;

export default function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const { login } = useAuth();
  const [tab, setTab] = useState("password"); // "password" | "magic"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const startResendCountdown = () => {
    setResendIn(RESEND_SECONDS);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendIn((s) => {
        if (s <= 1) { clearInterval(timerRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const goHome = (user) => {
    const desired = backTarget(loc.search, userHome(user));
    const gated = resolveRoute({ user, route: desired });
    nav(gated || desired, { replace: true });
  };

  const onSubmitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      goHome(user);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const onSubmitMagic = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      // Backend always answers 202 (no account enumeration). We show the same
      // "check your inbox" state regardless.
      await api.post("/auth/magic-link", { email: email.trim() });
      setMagicSent(true);
      startResendCountdown();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-brand-bg flex items-center justify-center px-6 py-12 relative overflow-hidden"
      data-testid="login-page"
    >
      <div className="absolute inset-0 starfield opacity-90 pointer-events-none" />
      <div className="absolute inset-0 bloom-top pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-10">
          <Link to="/"><BrandLogo size="lg" /></Link>
        </div>
        <div className="glass-card rounded-3xl p-8 sm:p-10">
          {magicSent ? (
            <MagicSent
              email={email}
              resendIn={resendIn}
              onResend={onSubmitMagic}
              onBack={() => { setMagicSent(false); setTab("magic"); }}
              loading={loading}
            />
          ) : (
            <>
              <h1 className="font-display text-3xl text-white mb-2 tracking-tight">Bem-vinda de volta</h1>
              <p className="text-[14px] text-brand-ink2 mb-6">
                Entre para continuar onde você parou.
              </p>

              {/* Method toggle */}
              <div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-brand-elevated border border-brand-line mb-6" role="tablist">
                <TabButton active={tab === "password"} onClick={() => setTab("password")} icon={Lock} label="Senha" testid="login-tab-password" />
                <TabButton active={tab === "magic"} onClick={() => setTab("magic")} icon={Sparkles} label="Link mágico" testid="login-tab-magic" />
              </div>

              {tab === "password" ? (
                <form onSubmit={onSubmitPassword} className="space-y-5" data-testid="login-password-form">
                  <AuthField
                    label="E-mail" type="email" name="email" value={email} onChange={setEmail}
                    testid="login-email" autoComplete="email" required
                  />
                  <AuthField
                    label="Senha" type="password" name="password" value={password} onChange={setPassword}
                    testid="login-password" autoComplete="current-password" required
                  />
                  <div className="flex justify-end -mt-2">
                    <Link
                      to="/esqueci-senha"
                      data-testid="login-forgot-link"
                      className="text-[12.5px] text-brand-ink2 hover:text-brand-rose font-ui transition-colors"
                    >
                      Esqueci minha senha
                    </Link>
                  </div>
                  <button
                    type="submit"
                    data-testid="login-submit"
                    disabled={loading}
                    className="pill-pink w-full py-3.5 text-[14px] tracking-wide"
                  >
                    {loading ? "Entrando..." : "Entrar"}
                  </button>
                </form>
              ) : (
                <form onSubmit={onSubmitMagic} className="space-y-5" data-testid="login-magic-form">
                  <AuthField
                    label="E-mail" type="email" name="email" value={email} onChange={setEmail}
                    testid="login-magic-email" autoComplete="email" required
                  />
                  <p className="text-[12.5px] text-brand-ink2 -mt-1">
                    Enviamos um link de acesso pro seu e-mail — sem precisar de senha.
                  </p>
                  <button
                    type="submit"
                    data-testid="login-magic-submit"
                    disabled={loading}
                    className="pill-pink w-full py-3.5 text-[14px] tracking-wide"
                  >
                    {loading ? "Enviando..." : "Enviar link mágico"}
                  </button>
                </form>
              )}

              <p className="text-[13px] text-brand-ink2 mt-7 text-center font-ui">
                Ainda não tem conta?{" "}
                <Link to="/signup" className="text-brand-rose hover:text-brand-roseHover" data-testid="link-signup">
                  Comece agora
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-1.5 py-2.5 rounded-full text-[13px] font-ui transition-colors min-h-[44px] ${
        active ? "bg-brand-rose text-white" : "text-brand-ink2 hover:text-white"
      }`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function MagicSent({ email, resendIn, onResend, onBack, loading }) {
  return (
    <div className="text-center" data-testid="login-magic-sent">
      <div className="w-14 h-14 rounded-full bg-brand-rose/12 border border-brand-rose/40 flex items-center justify-center mx-auto mb-5">
        <MailCheck className="w-6 h-6 text-brand-rose" />
      </div>
      <h1 className="font-display text-3xl text-white mb-2 tracking-tight">Enviamos um link pra você ✦</h1>
      <p className="text-[14px] text-brand-ink2 mb-1">
        Abra o e-mail enviado para{" "}
        <span className="text-brand-ink">{email}</span> e toque no link para entrar.
      </p>
      <p className="text-[12.5px] text-brand-ink3 mb-7">
        Não chegou? Veja a caixa de spam. O link expira em 15 minutos.
      </p>
      <button
        type="button"
        data-testid="login-magic-resend"
        onClick={onResend}
        disabled={resendIn > 0 || loading}
        className="w-full py-3.5 rounded-full bg-brand-elevated border border-brand-line text-brand-ink text-[14px] font-ui hover:border-brand-rose transition-colors disabled:opacity-50 min-h-[44px]"
      >
        {resendIn > 0 ? `Reenviar em ${resendIn}s` : loading ? "Reenviando..." : "Reenviar link"}
      </button>
      <button
        type="button"
        data-testid="login-magic-back"
        onClick={onBack}
        className="mt-3 text-[13px] text-brand-ink2 hover:text-brand-rose font-ui transition-colors"
      >
        Usar outro e-mail
      </button>
    </div>
  );
}
